<template>
	<div>
		<router-view />

		<BaseHeader :title="$t('assessor_case').toString()">
			<div class="pr-8 font-bold">
				Status:
				{{ damageAssessorCase?.status ? getBrokerCaseStatus(damageAssessorCase.status) : '-' }}
			</div>
		</BaseHeader>

		<div v-if="!isLoading && damageAssessorCase" class="main">
			<CardWrapper class="main">
				<DamageAssessorCaseInformationCard
					:damage-assessor-case="damageAssessorCase"
					:is-editable="isEditable"
					@editReferenceNumber="editReferenceNumber"
				/>

				<DamageAssessorCaseDamageAssessorCard
					:damage-assessor="damageAssessorCase.damage_assessor"
					:case-id="damageAssessorCase.id"
					:is-editable="isEditable"
				/>

				<DamageAssessorCasePersonCard :person="damageAssessorCase.person" :case-id="damageAssessorCase.id" :is-editable="isEditable" />

				<DamageAssessorCaseVehicleCard
					:vehicle="damageAssessorCase.vehicle"
					:vehicle-registration-document="vehicleRegistrationDocument"
					:case-id="damageAssessorCase.id"
					:is-editable="isEditable"
					@openDocuments="scrollToDocuments"
				/>

				<DamageAssessorCaseAccidentCard :damage-assessor-case="damageAssessorCase" :is-editable="isEditable" @openDocuments="scrollToDocuments" />

				<DamageAssessorCaseExaminationProgressCard
					:case-id="damageAssessorCase.id"
					:appointment-agreed-at="damageAssessorCase.appointment_agreed_at"
					:examined-at="damageAssessorCase.examined_at"
					:has-expertise="!!expertiseDocument"
					:is-editable="isEditable"
					@openDocuments="scrollToDocuments"
				/>

				<DamageAssessorCaseLawyerCard
					v-if="damageAssessorCase.broker_case?.lawyer_contact"
					:lawyer-contact="damageAssessorCase.broker_case.lawyer_contact"
					:show-broker-hint="true"
				/>

				<DamageAssessorCaseAssignedLawyerCard
					v-if="!damageAssessorCase.broker_case?.lawyer_contact"
					:lawyer-office="damageAssessorCase.lawyers_office"
					:is-editable="isEditable"
					@edit="editLawyerOffice"
				/>

				<DamageAssessorCasePhotosCard :damage-assessor-case="damageAssessorCase" :is-editable="isEditable" />

				<DamageAssessorCaseDocuments
					id="documentsection"
					class="col-span-2"
					style="scroll-margin-top: 80px"
					:damage-assessor-case="damageAssessorCase"
					:is-editable="isEditable"
					:active-tab="activeDocumentTab"
					@changeDocumentTab="changeDocumentTab"
					@updated="getCaseById"
				/>
			</CardWrapper>
		</div>

		<LoadingSpinner v-else />

		<Footer
			v-if="!isLoading && damageAssessorCase"
			@backButtonClick="goBack"
			:is-continue-button="canCloseCase"
			:is-skippable="false"
			:is-back-button="true"
			class="m-8"
		>
			<template #continueButton v-if="canCloseCase">
				<BaseButton @click="closeCase" :disabled="damageAssessorCase.status === 'closed'" class="btn-blue mr-6">
					{{ $t('close') }}
				</BaseButton>
			</template>
		</Footer>
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

import Footer from '@/components/damagecases/Footer.vue';
import CardWrapper from '@/components/damagecases/forms/CardWrapper.vue';

import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';

import DamageAssessorCaseInformationCard from '@/components/damageassessorcases/cards/DamageAssessorCaseInformationCard.vue';
import DamageAssessorCaseDamageAssessorCard from '@/components/damageassessorcases/cards/DamageAssessorCaseDamageAssessorCard.vue';
import DamageAssessorCasePersonCard from '@/components/damageassessorcases/cards/DamageAssessorCasePersonCard.vue';
import DamageAssessorCaseVehicleCard from '@/components/damageassessorcases/cards/DamageAssessorCaseVehicleCard.vue';
import DamageAssessorCaseExaminationProgressCard from '@/components/damageassessorcases/cards/DamageAssessorCaseExaminationProgressCard.vue';
import DamageAssessorCaseAccidentCard from '@/components/damageassessorcases/cards/DamageAssessorCaseAccidentCard.vue';
import DamageAssessorCaseLawyerCard from '@/components/damageassessorcases/cards/DamageAssessorCaseLawyerCard.vue';
import DamageAssessorCasePhotosCard from '@/components/damageassessorcases/cards/DamageAssessorCasePhotosCard.vue';

import { getBrokerCaseStatus } from '@/helpers/Utils';
import DamageAssessorCaseDocuments from '@/components/damageassessorcases/documents/DamageAssessorCaseDocuments.vue';
import Authentication from '@/mixins/Authentication';
import DamageAssessorCaseAssignedLawyerCard from '@/components/damageassessorcases/cards/DamageAssessorCaseAssignedLawyerCard.vue';

export default {
	name: 'DamageAssessorCaseDetail',
	mixins: [Authentication],
	components: {
		DamageAssessorCaseAssignedLawyerCard,
		DamageAssessorCaseDocuments,
		DamageAssessorCaseInformationCard,
		DamageAssessorCaseDamageAssessorCard,
		DamageAssessorCasePersonCard,
		DamageAssessorCaseVehicleCard,
		DamageAssessorCaseExaminationProgressCard,
		DamageAssessorCaseAccidentCard,
		DamageAssessorCaseLawyerCard,
		DamageAssessorCasePhotosCard,

		CardWrapper,
		LoadingSpinner,
		BaseButton,
		Footer,
		BaseHeader,
	},

	data() {
		return {
			isLoading: true,
			damageAssessorCase: null,
			activeDocumentTab: null,
		};
	},

	computed: {
		isEditable() {
			return true; //this.damageAssessorCase?.status !== 'closed';
		},

		canCloseCase() {
			return !!(
				this.damageAssessorCase?.appointment_agreed_at &&
				this.damageAssessorCase?.examined_at &&
				this.expertiseDocument !== null &&
				this.expertiseDocument.status !== 'closed'
			);
		},

		expertiseDocument() {
			const media = this.damageAssessorCase?.media ?? [];

			return media.find(item => item.category === 'gutachten') ?? null;
		},
		vehicleRegistrationDocument() {
			const media = this.damageAssessorCase?.media ?? [];
			let vehicleRegistrationDocument = media.find(item => item.category === 'vehicle_registration_document') ?? null;
			if (vehicleRegistrationDocument === null && this.damageAssessorCase?.broker_case !== null) {
				const brokerMedia = this.damageAssessorCase?.broker_case.media ?? [];
				vehicleRegistrationDocument = brokerMedia.find(item => item.category === 'vehicle_registration_document') ?? null;
			}
			return vehicleRegistrationDocument;
		},
		defaultDocumentTab() {
			if (this.isUserAllowedInLevel2) {
				return 'damageassessor';
			}

			return 'customer';
		},
	},

	mounted() {
		this.getCaseById();

		this.$root.$on('updateCase', this.getCaseById);
	},

	beforeDestroy() {
		this.$root.$off('updateCase', this.getCaseById);
	},

	methods: {
		getBrokerCaseStatus,
		getCaseById() {
			this.isLoading = true;

			return new DamageAssessorCaseRepository()
				.getDamageAssessorCaseDetail(this.$route.params.id)
				.then(response => {
					this.damageAssessorCase = response.data;

					if (!this.activeDocumentTab) {
						this.activeDocumentTab = this.defaultDocumentTab;
					}
				})
				.catch(error => {})
				.finally(() => {
					this.isLoading = false;
				});
		},
		scrollToDocuments(tab = null) {
			if (tab) {
				this.changeDocumentTab(tab);
			}

			this.$nextTick(() => {
				const documentSection = document.getElementById('documentsection');

				if (!documentSection) {
					return;
				}

				documentSection.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			});
		},

		changeDocumentTab(tab) {
			if (!tab) {
				return;
			}

			this.activeDocumentTab = tab;
		},
		editReferenceNumber() {
			if (!this.isEditable || !this.damageAssessorCase) {
				return;
			}

			this.$swal({
				title: this.$t('damage_assessor_file_number').toString(),

				input: 'text',

				inputValue: this.damageAssessorCase.reference_number ?? '',

				showCancelButton: true,

				confirmButtonText: this.$t('general.save').toString(),

				cancelButtonText: this.$t('general.cancel').toString(),

				inputValidator: value => {
					if (!value || !value.trim()) {
						return this.$t('please_check_input').toString();
					}

					return null;
				},
			}).then(result => {
				if (!result.isConfirmed) {
					return;
				}

				const referenceNumber = result.value?.trim();

				if (referenceNumber === this.damageAssessorCase.reference_number) {
					return;
				}

				this.updateReferenceNumber(referenceNumber);
			});
		},
		editLawyerOffice() {
			if (!this.isEditable) {
				return;
			}
			this.$router.push({
				name: 'damageAssessorCaseEditLawyer',
				params: {
					id: this.damageAssessorCase.id,
				},
			});
		},
		updateReferenceNumber(referenceNumber) {
			const payload = {
				damage_assessor_case: {
					reference_number: referenceNumber,
				},
			};

			new DamageAssessorCaseRepository()
				.updateDamageAssessorCase(this.damageAssessorCase.id, payload)
				.then(() => {
					return this.getCaseById();
				})
				.then(() => {
					this.$swal(
						this.$t('general.success').toString(),

						this.$t('general.changes_saved').toString(),

						'success',
					);
				})
				.catch(error => {
					console.log(error);

					this.$swal(
						this.$t('general.error').toString(),

						this.$t('general.changes_not_saved').toString(),

						'error',
					);
				});
		},

		closeCase() {
			new DamageAssessorCaseRepository()
				.closeDamageAssessorCase(this.$route.params.id)
				.then(() => {
					this.$swal(
						this.$t('general.success').toString(),

						this.$t('case_closed_successfully').toString(),

						'success',
					).then(() => {
						this.$router.push({
							name: 'damageAssessorCaseOverview',
						});
					});
				})
				.catch(() => {
					this.$swal(
						this.$t('general.error').toString(),

						this.$t('case_cannot_be_closed').toString(),

						'error',
					);
				});
		},

		goBack() {
			this.$router.push({
				name: 'damageAssessorCaseOverview',
			});
		},
	},
};
</script>

<style scoped></style>
